import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GoogleMap, GoogleMapsModule, MapAdvancedMarker } from '@angular/google-maps';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, switchMap } from 'rxjs';

import { QuickFilterPillsComponent } from '~components/quick-filter-pills/quick-filter-pills.component';
import { GOOGLE_MAP_ID, SELECTED_STATION_ZOOM } from '~constants/map.constants';
import type { Station } from '~models/station.model';
import { MapService } from '~services/map-service';
import { StationsService } from '~services/station.service';
import { BusinessUnit } from '~shared/business-unit';

@Component({
  selector: 'app-map-view',
  imports: [GoogleMap, MapAdvancedMarker, GoogleMapsModule, QuickFilterPillsComponent],
  templateUrl: './map-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapViewComponent {
  private readonly mapService = inject(MapService);
  private readonly stationsService = inject(StationsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  readonly bu = input<BusinessUnit>(BusinessUnit.US); // query parameter input
  readonly singleStationMode = input<boolean>(false);
  readonly stations = input.required<Station[]>();
  protected readonly mapElement = viewChild.required<GoogleMap>('storeMapId');
  protected readonly markers = computed(() =>
    this.mapService.getCustomMarkers(this.stations(), this.stationsService.selectedStation()?.id),
  );
  protected readonly mapConfig = computed(() => this.mapService.mapConfig());
  private readonly stationIds = computed(() => this.stations().map((s) => s.id));
  protected readonly GOOGLE_MAP_ID = GOOGLE_MAP_ID;

  constructor() {
    effect(() => {
      this.mapService.mapConfig.set(this.mapService.getMapConfigPerBu(this.bu()));
    });

    // Center map on selected station
    effect(() => {
      const selectedStation = this.stationsService.selectedStation();

      if (selectedStation) {
        const { latitude, longitude } = selectedStation;

        this.mapService.navigateOnDemand({ lat: latitude, lng: longitude }, SELECTED_STATION_ZOOM);
      }
    });
  }

  ngOnInit(): void {
    this.navigateOnGeolocation();

    // Fetch stations each time map is moved (mapBounds changed)
    this.stationsService.mapBounds$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(200),
        switchMap(() => this.stationsService.fetchStations()),
      )
      .subscribe();
  }

  onMapInitialized(): void {
    this.mapService.onMapInitialized(this.mapElement());
  }

  onBoundsChanged(): void {
    if (!this.mapService.mapInitialized() || this.mapElement() == null) {
      return;
    }

    const bounds = this.mapElement().getBounds()?.toJSON();

    if (!bounds) return;
    this.stationsService.mapBounds$.next(bounds);
  }

  onMarkerClick(index: number): void {
    const stationId = this.stationIds()[index];
    const station = this.stations().find((s) => s.id === stationId);

    if (!station) {
      console.warn(
        `Couldn't find station after clicking on a station marker, index: ${index}, stationId: ${stationId}`,
      );

      return;
    }

    //Close filters view, when user clicks on a station marker.
    this.stationsService.isFiltersViewOpened.set(false);
    this.stationsService.selectStation(station);

    if (this.singleStationMode()) {
      this.router.navigate(['..', station.id], { relativeTo: this.activatedRoute, queryParamsHandling: 'preserve' });
    }
  }

  private navigateOnGeolocation(): void {
    if (this.singleStationMode()) {
      return;
    }

    this.mapService.navigateToUserPosition();
  }
}
